import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Stack,
  Chip,
  alpha,
  Checkbox,
  ListItemText,
  Skeleton,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Inventory2 as InventoryIcon,
  Cancel as CancelIcon,
  Apartment as ApartmentIcon,
} from '@mui/icons-material';
import DashboardHeader from '../common/DashboardHeader';
import DashboardReportHeader from '../common/DashboardReportHeader';
import DashboardChartCard from '../common/DashboardChartCard';
import DashboardMetricCard from '../common/DashboardMetricCard';
import { getOrganizationStatistics } from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import {
  getInsightsChartPrimaryGridProps,
  getInsightsChartSecondaryGridProps,
  getInsightsGridSpacing,
  getInsightsMetricGridProps,
} from '../../utils/insightsDashboardLayout';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const splitBookingsWithinCapacity = (directBooked, onlineBooked, capacity) => {
  const direct = Number(directBooked || 0);
  const online = Number(onlineBooked || 0);
  const totalBooked = direct + online;
  const safeCapacity = Math.max(Number(capacity || 0), 0);

  if (totalBooked <= safeCapacity || totalBooked <= 0) {
    return {
      direct_booked: direct,
      online_booked: online,
      overbooked: 0,
    };
  }

  const cappedDirect = Math.min(direct, safeCapacity);
  const cappedOnline = Math.min(online, Math.max(safeCapacity - cappedDirect, 0));

  return {
    direct_booked: cappedDirect,
    online_booked: cappedOnline,
    overbooked: totalBooked - safeCapacity,
  };
};

const getHotelTotalRooms = (stats) =>
  Number(stats?.total_rooms ?? stats?.total_inventory ?? 0);

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

/**
 * Landing Org Dashboard Component
 * Comprehensive organization-level dashboard with aggregated data from all hotels
 */
const LandingOrgDashboard = () => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [loading, setLoading] = useState(true);
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [hotels, setHotels] = useState([]); // Always preserve all hotels - never filter this list
  const [selectedHotelIds, setSelectedHotelIds] = useState([]);
  const [startDate, setStartDate] = useState(() => moment().startOf('month'));
  const [endDate, setEndDate] = useState(() => moment().endOf('month'));
  const [pdfExporting, setPdfExporting] = useState(false);
  const isDark = currentTheme === 'dark';
  const hotelsInitialized = useRef(false);
  const initialFetchDone = useRef(false);
  const debounceTimer = useRef(null);
  const isFetching = useRef(false); // Prevent concurrent fetches
  const reportPageOneRef = useRef(null);
  const reportPageTwoRef = useRef(null);
  const exportInProgressRef = useRef(false);

  // Watch theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      isFetching.current = false;
    };
  }, []);

  // Fetch hotels first (without dates)
  const fetchHotels = useCallback(async () => {
    if (isFetching.current) {
      return;
    }

    isFetching.current = true;
    setLoadingHotels(true);
    setError(null);
    
    try {
      // Fetch with default date range just to get hotels list
      const defaultStart = moment().startOf('month');
      const defaultEnd = moment().endOf('month');
      const data = await getOrganizationStatistics(
        null, // All hotels
        defaultStart.format('YYYY-MM-DD'),
        defaultEnd.format('YYYY-MM-DD')
      );
      
      const newHotels = data.hotels || [];
      const sortedHotels = newHotels.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setHotels(sortedHotels);
      
      // Auto-select all hotels
      if (sortedHotels.length > 0) {
        const allHotelIds = sortedHotels.map(hotel => hotel.id);
        setSelectedHotelIds(allHotelIds);
        hotelsInitialized.current = true;
      }
      
    } catch (err) {
      const responseData = err.responseData || err.response?.data || {};
      const statusCode = err.statusCode || err.response?.status;
      let errorMessage = 'Failed to load hotels';
      
      if (err.userMessage) {
        errorMessage = err.userMessage;
      } else if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      } else if (responseData.message) {
        errorMessage = responseData.message;
      }
      
      setError({ title: 'Error Loading Hotels', message: errorMessage });
    } finally {
      setLoadingHotels(false);
      isFetching.current = false;
    }
  }, []);

  // Fetch statistics data (after hotels are loaded)
  const fetchData = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetching.current) {
      return;
    }

    // Don't fetch if hotels aren't loaded yet or dates aren't set
    if (!hotelsInitialized.current || !startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
      return;
    }

    if (startDate.isAfter(endDate)) {
      setError({
        title: 'Invalid Date Range',
        message: 'Start date must be before or equal to end date. Please adjust your date selection.',
      });
      return;
    }

    isFetching.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const data = await getOrganizationStatistics(
        selectedHotelIds.length > 0 ? selectedHotelIds : null,
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      setStatistics(data.statistics);
    } catch (err) {
      // Enhanced error handling with detailed logging
      const responseData = err.responseData || err.response?.data || {};
      const statusCode = err.statusCode || err.response?.status;
      
      // Extract error message from various possible locations
      let errorMessage = 'Failed to load organization statistics';
      if (err.userMessage) {
        errorMessage = err.userMessage;
      } else if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      } else if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.detail) {
        errorMessage = responseData.detail;
      } else if (err.message && !err.message.includes('status code')) {
        errorMessage = err.message;
      }
      
      const errorDetails = {
        message: errorMessage,
        statusCode: statusCode,
        responseData: responseData,
        timestamp: new Date().toISOString(),
      };
      
      // Build user-friendly error message
      let errorTitle = 'Error Loading Dashboard';
      
      // Remove generic error messages
      if (errorMessage.includes('Request failed') || errorMessage.includes('status code')) {
        errorMessage = null; // Will be replaced below
      }
      
      // Add status code information if available
      if (statusCode) {
        if (statusCode === 400) {
          errorTitle = 'Unable to Load Dashboard';
          // Use the extracted error message or provide a default
          if (!errorMessage || errorMessage.trim() === '') {
            if (responseData.error) {
              errorMessage = responseData.error;
            } else if (responseData.message) {
              errorMessage = responseData.message;
            } else if (responseData.detail) {
              errorMessage = responseData.detail;
            } else {
              errorMessage = 'Invalid request. Please check your filters and try again.';
            }
          }
        } else if (statusCode === 401) {
          errorTitle = 'Authentication Required';
          errorMessage = 'Please log in again to access the dashboard.';
        } else if (statusCode === 403) {
          errorTitle = 'Access Denied';
          errorMessage = 'You do not have permission to access this data. Please contact your administrator.';
        } else if (statusCode === 404) {
          errorTitle = 'No Data Found';
          errorMessage = 'No data found for the selected criteria. Please try adjusting your date range or hotel filter.';
        } else if (statusCode === 500) {
          errorTitle = 'Server Error';
          errorMessage = 'A server error occurred. Please try again later or contact support if the problem persists.';
        } else {
          errorMessage = `${errorMessage} (Status: ${statusCode})`;
        }
      }
      
      // For 400 errors, provide more helpful context
      if (statusCode === 400 && (errorMessage.toLowerCase().includes('organization') || errorMessage.toLowerCase().includes('organisation'))) {
        errorMessage = 'No organization found. Please ensure you are associated with a hotel. If you believe this is an error, please contact your administrator.';
      }
      
      setError({ title: errorTitle, message: errorMessage });
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [startDate, endDate, selectedHotelIds]);

  // Initial fetch - load hotels first
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchHotels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Fetch statistics after hotels are loaded and dates are set
  useEffect(() => {
    // Wait for hotels to be initialized and dates to be set
    if (!hotelsInitialized.current || !startDate || !endDate) {
      return;
    }

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Debounce the fetch to avoid too many API calls
    debounceTimer.current = setTimeout(() => {
      if (!isFetching.current) {
        fetchData();
      }
    }, 300);
    
    // Cleanup timer on unmount or dependency change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedHotelIds]); // Only fetch when these change

  const handleHotelChange = useCallback((event) => {
    const value = event.target.value;
    // Material-UI Select with multiple returns an array
    // Convert string IDs to numbers to match hotel.id type
    const selectedIds = typeof value === 'string' 
      ? value.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id))
      : Array.isArray(value) 
        ? value.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id))
        : [];
    
    // Always preserve selection - don't filter based on hotels list
    // The hotels list should always contain all hotels
    setSelectedHotelIds(selectedIds);
  }, []); // No dependencies - hotels list should never change in a way that affects selection

  const handleDateRangeChange = useCallback((nextStartDate, nextEndDate) => {
    if (nextStartDate?.isValid?.() && nextEndDate?.isValid?.()) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
    }
  }, []);

  // Theme-aware colors
  const getThemeColors = () => {
    if (isDark) {
      return {
        booking: '#4ce1b6',
        revenue: '#ff6b7d',
        expense: '#f8e082',
        hotelDue: '#8e7dff',
        profit: '#66d4a8',
        inventory: '#8ac9ff',
        directBooked: '#66d4a8',
        onlineBooked: '#f8e082',
        overbooked: '#ff4861',
        customer: '#f6da6e',
        cancelled: '#ff4861',
        active: '#66d4a8',
      };
    } else {
      return {
        booking: '#4ce1b6',
        revenue: '#ff4861',
        expense: '#f6da6e',
        hotelDue: '#8e7dff',
        profit: '#4ce1b6',
        inventory: '#70bbfd',
        directBooked: '#4ce1b6',
        onlineBooked: '#f6da6e',
        overbooked: '#ff4861',
        customer: '#f6da6e',
        cancelled: '#ff4861',
        active: '#4ce1b6',
      };
    }
  };

  const themeColors = getThemeColors();
  const unifiedChartColors = isDark
    ? {
        red: '#ff6b7d',
        green: '#4ce1b6',
        yellow: '#f8e082',
      }
    : {
        red: '#ff4861',
        green: '#4ce1b6',
        yellow: '#f6da6e',
      };

  // Memoize chart data preparation for performance
  const chartData = useMemo(() => {
    const sourceStatistics = statistics || {};
    const dailyStats = sourceStatistics.daily_stats || [];
    const dailyStatsByDate = dailyStats.reduce((acc, stat) => {
      if (stat?.date) acc[stat.date] = stat;
      return acc;
    }, {});
    const fullRangeStats = [];
    if (startDate?.isValid?.() && endDate?.isValid?.()) {
      const currentDate = startDate.clone().startOf('day');
      const lastDate = endDate.clone().startOf('day');
      while (currentDate.isSameOrBefore(lastDate)) {
        const dateKey = currentDate.format('YYYY-MM-DD');
        fullRangeStats.push({
          ...(dailyStatsByDate[dateKey] || {}),
          date: dateKey,
          name: currentDate.format('MMM DD'),
        });
        currentDate.add(1, 'day');
      }
    }

    return {
      bookingStatsData: fullRangeStats.map(stat => ({
        name: stat.name,
        bookings: stat.bookings || 0,
        active: stat.active_bookings || 0,
        cancelled: stat.cancelled_bookings || 0,
        booking: Math.max(
          (stat.bookings || 0) - (stat.active_bookings || 0) - (stat.cancelled_bookings || 0),
          0
        ),
      })),
      financeStatsData: fullRangeStats.map(stat => ({
        name: stat.name,
        revenue: stat.revenue || 0,
        expense: stat.expense || 0,
        hotelDue: stat.hotel_due || 0,
        profit: stat.profit || 0,
      })),
      inventoryStatsData: fullRangeStats.map(stat => {
        const hotelTotalRooms = getHotelTotalRooms(sourceStatistics);
        const dayTotal = Number(stat.total_hotel_rooms ?? stat.total ?? hotelTotalRooms ?? 0);
        const rawDirectBooked = stat.direct_booked || 0;
        const rawOnlineBooked = stat.online_booked || 0;
        const bookingSplit = splitBookingsWithinCapacity(rawDirectBooked, rawOnlineBooked, dayTotal);
        const booked = stat.rooms_booked ?? stat.booked_rooms ?? (rawDirectBooked + rawOnlineBooked);
        return {
          name: stat.name,
          total: dayTotal,
          direct_booked: bookingSplit.direct_booked,
          online_booked: bookingSplit.online_booked,
          overbooked: bookingSplit.overbooked,
          booked,
          rooms_booked: booked,
          available: dayTotal > 0 ? Math.max(dayTotal - Number(booked || 0), 0) : 0,
        };
      }),
    };
  }, [statistics, startDate, endDate, themeColors]);

  // Prepare chart data with fallback
  const bookingStatsData = chartData?.bookingStatsData || [];
  const financeStatsData = chartData?.financeStatsData || [];
  const inventoryStatsData = chartData?.inventoryStatsData || [];

  const formatCurrency = (val) => {
    return `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

  const bookingSourcePalette = ['#4ce1b6', '#f6da6e', '#70bbfd', '#ff4861', '#8e7dff', '#ff9f43', '#26c6da', '#66bb6a'];
  const bookingSourceColorMap = {
    Direct: '#4ce1b6',
    Reservation: '#f6da6e',
    'Booking.com': '#2563eb',
    Agoda: '#8e24aa',
    MakeMyTrip: '#ef4444',
    MMT: '#ef4444',
    WalkIn: '#70bbfd',
    'Walk-In': '#70bbfd',
  };
  const bookingSourceChartData = (statistics?.booking_source_breakdown || [])
    .map((entry, idx) => {
      const name = String(entry?.source || entry?.name || entry?.channel || '').trim();
      return {
        name,
        value: Number(entry?.value) || 0,
        color: bookingSourceColorMap[name] || bookingSourcePalette[idx % bookingSourcePalette.length],
      };
    })
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);
  const totalSourceBookings = bookingSourceChartData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const paymentMethodPalette = ['#4ce1b6', '#70bbfd', '#f6da6e', '#ff9f43', '#ff4861', '#8e7dff', '#26c6da', '#ef5350', '#66bb6a', '#ab47bc', '#ffa726', '#78909c'];
  const paymentMethodColorMap = {
    Cash: '#4ce1b6',
    'Credit Card': '#ff4861',
    'Debit Card': '#70bbfd',
    'Bank Transfer': '#8e7dff',
    UPI: '#f6da6e',
    'Digital Wallet': '#26c6da',
    Cheque: '#ff9f43',
    Other: '#ab47bc',
    '—': '#78909c',
  };
  const paymentMethodChartData = (statistics?.payment_method_breakdown || [])
    .map((entry, idx) => {
      const name = String(entry?.method || entry?.name || entry?.payment_method || '').trim();
      return {
        name,
        value: Number(entry?.value) || 0,
        color: paymentMethodColorMap[name] || paymentMethodPalette[idx % paymentMethodPalette.length],
      };
    })
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);
  const totalCollection = paymentMethodChartData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const selectedRangeDays = startDate && endDate ? Math.max(endDate.diff(startDate, 'days') + 1, 1) : 1;
  const totalRooms = Number(statistics?.total_room_capacity ?? ((statistics?.total_inventory || 0) * selectedRangeDays));
  const bookedRooms = Number(statistics?.total_booked_rooms || 0);
  const occupancyPercentage = totalRooms > 0 ? (bookedRooms / totalRooms) * 100 : 0;
  const displayStatistics = statistics || {};
  const dashboardLoading = loading || loadingHotels;

  // Memoize sorted hotels list for performance
  const sortedHotels = useMemo(() => {
    return [...hotels].sort((a, b) => {
      // Sort by name alphabetically
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [hotels]);

  // Memoize selected hotels label for performance
  const selectedHotelsLabel = useMemo(() => {
    if (selectedHotelIds.length === 0) {
      return 'All Hotels';
    }
    if (selectedHotelIds.length === 1) {
      const hotel = hotels.find(h => h.id === selectedHotelIds[0]);
      return hotel ? hotel.name : '1 hotel selected';
    }
    if (selectedHotelIds.length === hotels.length) {
      return 'All Hotels';
    }
    return `${selectedHotelIds.length} hotels selected`;
  }, [selectedHotelIds, hotels]);

  const handleDownloadPdf = useCallback(async () => {
    if (
      !statistics ||
      loading ||
      exportInProgressRef.current ||
      !reportPageOneRef.current ||
      !reportPageTwoRef.current
    ) return;
    exportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setPdfExporting(true);
    try {
      const selectedLabelForFile = selectedHotelsLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'hotels';
      const filename = `organization-performance-${selectedLabelForFile}-${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.pdf`;
      const rangeLabel = `${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`;

      await new Promise((resolve) => setTimeout(resolve, 120));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (document.fonts?.ready) await document.fonts.ready;

      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const drawPdfHeader = (pageNumber) => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Organization Performance Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`${selectedHotelsLabel} | ${rangeLabel} | Generated ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageNumber}`, pageWidth / 2, 23.6, { align: 'center' });
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };

      const drawPdfFooter = (pageNumber) => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Professional multi-hotel performance intelligence for smarter operations.', margin, footerTop + 9.2);
        pdf.text(`Page ${pageNumber}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };

      const captureElement = (element) => html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (documentClone, clonedElement) => {
          documentClone.body.style.backgroundColor = '#ffffff';
          if (clonedElement) clonedElement.style.backgroundColor = '#ffffff';
        },
      });

      const addCanvasToPdf = (canvas, { fitSinglePage = false, forceNewPage = false } = {}) => {
        if (forceNewPage) {
          pdf.addPage();
          pageIndex += 1;
        }
        const imageData = canvas.toDataURL('image/png');
        if (fitSinglePage) {
          const widthByHeight = (canvas.width * printableHeight) / canvas.height;
          const imageWidth = Math.min(maxImageWidth, widthByHeight);
          const imageHeight = (canvas.height * imageWidth) / canvas.width;
          const imageLeft = (pageWidth - imageWidth) / 2;
          drawPdfHeader(pageIndex);
          pdf.addImage(imageData, 'PNG', imageLeft, imageTop, imageWidth, imageHeight);
          drawPdfFooter(pageIndex);
          return;
        }

        const imageWidth = maxImageWidth;
        const imageHeight = (canvas.height * imageWidth) / canvas.width;
        const imageLeft = (pageWidth - imageWidth) / 2;
        let renderedHeight = 0;
        while (renderedHeight < imageHeight) {
          drawPdfHeader(pageIndex);
          pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
          drawPdfFooter(pageIndex);
          renderedHeight += printableHeight;
          if (renderedHeight < imageHeight) {
            pdf.addPage();
            pageIndex += 1;
          }
        }
      };

      const pageOneCanvas = await captureElement(reportPageOneRef.current);
      const pageTwoCanvas = await captureElement(reportPageTwoRef.current);
      addCanvasToPdf(pageOneCanvas, { fitSinglePage: true });
      addCanvasToPdf(pageTwoCanvas, { forceNewPage: true });

      const formatPdfAmount = (value) =>
        Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const formatPaymentBreakdown = (total, methods = []) => {
        const validMethods = (methods || []).filter((item) => Number(item?.value || 0) > 0);
        const totalLabel = formatPdfAmount(total || 0);
        if (!validMethods.length) return totalLabel;
        const detail = validMethods
          .map((item) => `${item.method || item.name || '-'} - ${formatPdfAmount(item.value)}`)
          .join('\n');
        return `${totalLabel}\n${detail}`;
      };
      const hotelTotalRooms = getHotelTotalRooms(statistics);
      const reportTableRows = (statistics?.daily_stats || []).map((stat) => {
        const roomTotal = Number(stat.total_hotel_rooms ?? stat.total ?? hotelTotalRooms ?? 0);
        const roomsBookedValue = Number(stat.rooms_booked ?? stat.booked_rooms ?? 0);
        const roomPct = roomTotal > 0 ? ((roomsBookedValue / roomTotal) * 100).toFixed(1) : '0.0';
        return [
          moment(stat.date).format('DD MMM YYYY'),
          Number(stat.bookings || 0).toString(),
          `${roomsBookedValue}/${roomTotal} (${roomPct}%)`,
          formatPdfAmount(stat.expense),
          formatPdfAmount(stat.hotel_due),
          formatPaymentBreakdown(stat.revenue, stat.hotel_sale_payment_methods),
        ];
      });

      if (reportTableRows.length) {
        pdf.addPage();
        pageIndex += 1;
        drawPdfHeader(pageIndex);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Daily Organization Performance Details', margin, imageTop);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Aggregated date-wise bookings, room pickup, expenses, hotel due, and hotel sales.', margin, imageTop + 5);
        drawPdfFooter(pageIndex);

        autoTable(pdf, {
          startY: imageTop + 10,
          head: [[
            'Date',
            'Booking',
            'Booked Room / Total Room',
            'Hotel Expense',
            'Hotel Due',
            'Hotel Sale',
          ]],
          body: reportTableRows,
          margin: { top: imageTop, right: margin, bottom: footerHeight + 7, left: margin },
          styles: {
            font: 'helvetica',
            fontSize: 6.5,
            cellPadding: 1.4,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
            textColor: [51, 65, 85],
          },
          headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index > 0) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.textColor = [15, 23, 42];
            }
          },
          didDrawPage: () => {
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
            drawPdfHeader(currentPage);
            drawPdfFooter(currentPage);
          },
        });
      }

      pdf.save(filename);
    } finally {
      document.body.classList.remove('dashboard-pdf-exporting');
      setPdfExporting(false);
      exportInProgressRef.current = false;
    }
  }, [statistics, loading, selectedHotelsLabel, startDate, endDate]);

  const metricGrid = getInsightsMetricGridProps();
  const chartPrimaryGrid = getInsightsChartPrimaryGridProps();
  const chartSecondaryGrid = getInsightsChartSecondaryGridProps();
  const gridSpacing = getInsightsGridSpacing();

  return (
    <Box 
      sx={{ 
        p: 3,
        minHeight: '100vh',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Organization Dashboard"
          forceDark={isMobileShellLayout()}
          breadcrumbs={[
            { label: 'Home', path: '/onboarding/landing/' },
            { label: 'Organization Dashboard' }
          ]}
          navLinks={[
            { url: '/onboarding/landing/?view=hotels', label: 'Hotelier' },
          ]}
        />
      </Box>

      <Stack spacing={2.5} sx={{ mb: 3 }}>
        <DashboardReportHeader
          title="Multi Hotel Performance Dashboard"
          subtitle="Select hotels and view the same performance intelligence aggregated across all selected properties."
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={handleDateRangeChange}
          onRefresh={fetchData}
          onPdfDownload={handleDownloadPdf}
          loading={loading}
          pdfDisabled={!statistics || loading || loadingHotels}
          themeMode={currentTheme}
          accentColor={themeColors.booking}
          pdfExporting={pdfExporting}
        />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            backgroundColor: isDark ? '#2a2a31' : '#ffffff',
            border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 280, backgroundColor: 'transparent' }}>
              <InputLabel sx={{ color: isDark ? '#999' : '#757575' }}>Select Hotels</InputLabel>
              <Select
                multiple
                value={selectedHotelIds}
                onChange={handleHotelChange}
                label="Select Hotels"
                renderValue={() => selectedHotelsLabel}
                sx={{
                  color: isDark ? '#ddd' : '#212121',
                  backgroundColor: 'transparent',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#333246' : '#e0e0e0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#555' : '#999' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.booking },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                      '& .MuiMenuItem-root': {
                        color: isDark ? '#ddd' : '#212121',
                        '&:hover': { backgroundColor: isDark ? '#38373f' : '#f5f5f5' },
                        '&.Mui-selected': {
                          backgroundColor: isDark ? '#38373f' : '#e3f2fd',
                          '&:hover': { backgroundColor: isDark ? '#444350' : '#bbdefb' },
                        },
                      },
                    },
                  },
                }}
              >
                {sortedHotels.length > 0 ? (
                  sortedHotels.map((hotel) => (
                    <MenuItem key={hotel.id} value={hotel.id}>
                      <Checkbox
                        checked={selectedHotelIds.includes(hotel.id)}
                        sx={{
                          color: isDark ? '#999' : '#757575',
                          '&.Mui-checked': { color: themeColors.booking },
                        }}
                      />
                      <ListItemText primary={hotel.name} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" sx={{ color: isDark ? '#999' : '#757575' }}>
                      No hotels available
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <Chip
              label={`${selectedHotelIds.length || sortedHotels.length} selected`}
              sx={{
                fontWeight: 800,
                color: isDark ? '#e7efff' : '#0f172a',
                bgcolor: alpha(themeColors.booking, isDark ? 0.16 : 0.12),
                border: `1px solid ${alpha(themeColors.booking, 0.28)}`,
              }}
            />
          </Box>
          <Card
            elevation={0}
            sx={{
              ml: { xs: 0, md: 'auto' },
              minWidth: { xs: '100%', sm: 260 },
              overflow: 'hidden',
              position: 'relative',
              borderRadius: 2.5,
              border: `1px solid ${alpha(themeColors.booking, isDark ? 0.34 : 0.28)}`,
              background: isDark
                ? `linear-gradient(135deg, ${alpha(themeColors.booking, 0.16)} 0%, rgba(35, 35, 41, 0.96) 58%, rgba(22, 28, 38, 0.98) 100%)`
                : `linear-gradient(135deg, ${alpha(themeColors.booking, 0.18)} 0%, #ffffff 56%, #f2fffb 100%)`,
              boxShadow: `0 18px 40px ${alpha(themeColors.booking, isDark ? 0.12 : 0.18)}`,
              px: 2,
              py: 1.4,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -34,
                right: -28,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: alpha(themeColors.booking, isDark ? 0.14 : 0.16),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#06251f',
                  background: `linear-gradient(180deg, ${themeColors.booking} 0%, ${alpha(themeColors.booking, 0.72)} 100%)`,
                  boxShadow: `0 12px 24px ${alpha(themeColors.booking, 0.28)}`,
                }}
              >
                <ApartmentIcon />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: isDark ? alpha('#ffffff', 0.72) : '#64748b',
                    fontWeight: 900,
                    letterSpacing: '0.12em',
                    lineHeight: 1,
                  }}
                >
                  YOUR ASSIGNED HOTELS
                </Typography>
                <Typography sx={{ mt: 0.35, fontSize: 28, fontWeight: 950, color: isDark ? '#f8fafc' : '#0f172a', lineHeight: 1 }}>
                  {loadingHotels ? (
                    <Skeleton variant="text" width={48} sx={{ bgcolor: alpha(themeColors.booking, 0.18) }} />
                  ) : (
                    formatNumber(sortedHotels.length)
                  )}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.45} alignItems="flex-end" sx={{ height: 32 }}>
                {[0.45, 0.7, 0.55, 0.9].map((height, index) => (
                  <Box
                    key={height}
                    sx={{
                      width: 7,
                      height: `${height * 100}%`,
                      borderRadius: 1,
                      bgcolor: alpha(themeColors.booking, 0.35 + index * 0.12),
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Stack>

      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert 
            severity="error"
            sx={{
              '& .MuiAlert-message': {
                width: '100%',
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
              },
            }}
            icon={false}
          >
            <Box>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 1,
                  color: 'error.main',
                  fontSize: '1.1rem',
                }}
              >
                {typeof error === 'object' ? error.title : 'Error Loading Dashboard'}
              </Typography>
              <Typography 
                variant="body1" 
                component="div"
                sx={{
                  color: 'text.primary',
                  lineHeight: 1.6,
                }}
              >
                {typeof error === 'object' ? error.message : error}
              </Typography>
              {typeof error === 'object' && error.message && error.message.includes('organization') && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    What you can do:
                  </Typography>
                  <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
                    <li>Ensure you are assigned to at least one hotel</li>
                    <li>Contact your administrator to verify your account setup</li>
                    <li>Try refreshing the page</li>
                  </Typography>
                </Box>
              )}
            </Box>
          </Alert>
        </Box>
      )}

      {!error && (statistics || dashboardLoading) && (
        <>
        <Box
          ref={reportPageOneRef}
          data-pdf-report-section
          sx={{
            p: { xs: 0.5, sm: 0 },
            backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
          }}
        >
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Total Revenue" value={displayStatistics.total_revenue || 0} unit="revenue" data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.revenue }))} color={themeColors.revenue} icon={<MoneyIcon sx={{ color: 'inherit' }} />} theme={currentTheme} formatValue={formatCurrency} />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Hotel expenses" value={displayStatistics.total_expense ?? 0} unit="hotel purchases" data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.expense }))} color={themeColors.expense} icon={<TrendingDownIcon sx={{ color: 'inherit' }} />} theme={currentTheme} formatValue={formatCurrency} />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Hotel Due" value={displayStatistics.total_hotel_due || displayStatistics.hotel_due_summary?.due || 0} unit="checkout due" data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.hotelDue }))} color={themeColors.hotelDue} icon={<TrendingDownIcon sx={{ color: 'inherit' }} />} theme={currentTheme} formatValue={formatCurrency} />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Total Profit" value={displayStatistics.total_profit || 0} unit="profit" data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.profit }))} color={themeColors.profit} icon={<TrendingUpIcon sx={{ color: 'inherit' }} />} theme={currentTheme} formatValue={formatCurrency} />
            </Grid>
          </Grid>

          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Total Bookings / Cancelled Bookings" value={`${displayStatistics.total_bookings || 0}/${displayStatistics.cancelled_bookings || 0}`} unit="bookings" data={bookingStatsData.map((stat) => ({ name: stat.name, value: stat.bookings }))} color={themeColors.cancelled} icon={<CancelIcon sx={{ color: 'inherit' }} />} theme={currentTheme} formatValue={(val) => val.toString()} />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Booked Room / Total Room" value={`${bookedRooms}/${totalRooms}`} unit="rooms" data={inventoryStatsData.map((stat) => ({ name: stat.name, value: Number(stat.rooms_booked || stat.booked || 0) }))} color={themeColors.active} icon={<InventoryIcon sx={{ color: 'inherit' }} />} theme={currentTheme} />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Occupancy Percentage" value={occupancyPercentage} unit="occupancy" data={inventoryStatsData.map((stat) => ({ name: stat.name, value: stat.total > 0 ? Number(((stat.rooms_booked / stat.total) * 100).toFixed(1)) : 0 }))} color={themeColors.inventory} icon={<InventoryIcon sx={{ color: 'inherit' }} />} theme={currentTheme} formatValue={(val) => `${Number(val || 0).toFixed(1)}%`} />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard variant="performance" loading={dashboardLoading} title="Total Customers" value={displayStatistics.total_customers || 0} unit="customers" data={[]} color={themeColors.customer} icon={<PeopleIcon sx={{ color: 'inherit' }} />} theme={currentTheme} formatValue={(val) => val.toString()} />
            </Grid>
          </Grid>

          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item {...chartPrimaryGrid}>
              <DashboardChartCard
                variant="performance"
                loading={dashboardLoading}
                eyebrow="FINANCE STATISTICS"
                subtitle="Revenue with hotel purchases and checkout due"
                data={financeStatsData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'revenue', name: 'Total Revenue', color: themeColors.revenue },
                  { dataKey: 'expense', name: 'Hotel expenses', color: themeColors.expense, stackId: 'expenses' },
                  { dataKey: 'hotelDue', name: 'Hotel Due', color: themeColors.hotelDue, stackId: 'expenses' },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={formatCurrency}
                barRadius={[0, 0, 0, 0]}
              />
            </Grid>
            <Grid item {...chartSecondaryGrid}>
              <DashboardChartCard variant="performance" loading={dashboardLoading} eyebrow="PAYMENT METHODS" subtitle={`Selected range collection: ${formatCurrency(totalCollection)}`} chartType="pie" pieData={paymentMethodChartData} pieAsBars pieShowValueInLabel theme={currentTheme} height={300} formatValue={formatCurrency} />
            </Grid>
          </Grid>

          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item {...chartPrimaryGrid}>
              <DashboardChartCard
                variant="performance"
                loading={dashboardLoading}
                eyebrow="BOOKING STATISTICS"
                subtitle="Bookings split by status over time"
                data={bookingStatsData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'active', name: 'Active Bookings', color: unifiedChartColors.green, stackId: 'bookingStatus' },
                  { dataKey: 'cancelled', name: 'Cancelled Bookings', color: unifiedChartColors.red, stackId: 'bookingStatus' },
                  { dataKey: 'booking', name: 'Booking', color: unifiedChartColors.yellow, stackId: 'bookingStatus' },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={(val) => val.toString()}
                showBarTotalTooltip
                barTotalLabel="Total Bookings"
                barRadius={[0, 0, 0, 0]}
              />
            </Grid>
            <Grid item {...chartSecondaryGrid}>
              <DashboardChartCard variant="performance" loading={dashboardLoading} eyebrow="BOOKING SOURCES" subtitle={`Total bookings: ${totalSourceBookings}`} chartType="pie" pieData={bookingSourceChartData} pieAsBars pieShowValueInLabel theme={currentTheme} height={300} formatValue={(val) => Number(val || 0).toString()} />
            </Grid>
          </Grid>
        </Box>

        <Box
          ref={reportPageTwoRef}
          data-pdf-report-section
          sx={{
            p: { xs: 0.5, sm: 0 },
            backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
          }}
        >
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item xs={12}>
              <DashboardChartCard
                variant="performance"
                loading={dashboardLoading}
                eyebrow="INVENTORY TRENDS"
                subtitle="Direct, OTA and overbooking inventory by date"
                data={inventoryStatsData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'direct_booked', name: 'Direct Bookings', color: themeColors.directBooked, stackId: 'inventory' },
                  { dataKey: 'online_booked', name: 'OTA Bookings', color: themeColors.onlineBooked, stackId: 'inventory' },
                  { dataKey: 'overbooked', name: 'Overbooking', color: themeColors.overbooked, stackId: 'inventory' },
                ]}
                theme={currentTheme}
                height={340}
                formatValue={(val) => val.toString()}
                barRadius={[0, 0, 0, 0]}
              />
            </Grid>
          </Grid>

        </Box>
        </>
      )}
    </Box>
  );
};

export default LandingOrgDashboard;

